/**
 * THE PAYMENT DISPUTE DESK, read from the database.
 *
 * Script 241 gave a tenant a way to file a dispute and a landlord a way to
 * answer one, and stated that only Orchid settles it. Script 242 built the two
 * functions that make that sentence true — and until this file nothing called
 * them, so a filed dispute carried both parties' testimony and no desk could
 * reach it. A tenant would have filed into silence.
 *
 * ---------------------------------------------------------------------------
 * THE PERMISSION IS `resolve_dispute`, NOT THE ONE THE POLICY NAMES
 *
 * `payment_disputes` carries an RLS policy gated on `fica_review`, which is
 * row two of the ledger's thirteen permissions "named by twenty-nine policies,
 * held by nobody" — no row in the catalogue, no role grant, so it can be
 * attached to neither a role nor a card. The same shape as
 * `manage_sanctions_list` before script 205 registered it.
 *
 * `resolve_dispute` IS catalogued — "Adjudicate payment, deposit and invoice
 * disputes", department Legal & Disputes, requires_reason_capture true — and
 * both 242 functions gate on it. So this desk needs no new permission invented;
 * it needs somebody to be granted the one that already exists, which is a
 * founder decision recorded in the ledger.
 *
 * ---------------------------------------------------------------------------
 * TWO CALLS, AND THE REASON IS A CONDITION OF BOTH
 *
 *   queue    — the open work list. It carries what each party wrote about a
 *              payment, which is identifiable personal data joined to a
 *              grievance, so opening the section is an act of access. Both
 *              functions log through `fn_log_my_admin_action` as their FIRST
 *              statement: a desk without the permission, or with a blank
 *              reason, never reaches a row. The ReasonGate stands in front of
 *              the fetch for the same reason it does on sanctions.
 *
 *   settle   — for the tenant, or for the landlord. The database refuses any
 *              third value, refuses a blank summary, refuses a second finding
 *              on a settled dispute, and DERIVES the score protection from the
 *              finding rather than taking it as an argument. None of those are
 *              this file's rules and it does not restate them.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const fail = (error) => ({ data: null, error });

const NOT_CONFIGURED =
  'This portal was built without its Supabase credentials, so it cannot reach the database.';

/**
 * The two findings, in the words a desk is choosing between.
 *
 * There is no third. A dispute a tenant withdrew is theirs to withdraw and is
 * not an outcome Orchid decides on their behalf, so it never appears here.
 */
export const FINDINGS = [
  {
    key: 'resolved_tenant',
    label: 'For the tenant',
    blurb:
      'The tenant was right. The disputed payment does not count against them, '
      + 'and their score protection stays on.',
  },
  {
    key: 'resolved_landlord',
    label: 'For the landlord',
    blurb:
      'The landlord was right. The protection lifts and the payment counts as it '
      + 'otherwise would have.',
  },
];

export const findingLabel = (key) =>
  (FINDINGS.find((f) => f.key === key) || { label: key }).label;

/** What each dispute type is, in a person's words rather than the column's. */
export const DISPUTE_TYPES = {
  payment_made_not_confirmed: 'Paid, not confirmed',
  payment_rejected_incorrectly: 'Rejected in error',
  banking_delay: 'Held up by the bank',
  wrong_amount_recorded: 'Wrong amount recorded',
  other: 'Something else',
};

export const disputeTypeLabel = (key) => DISPUTE_TYPES[key] || key;

/**
 * The open queue, oldest first.
 *
 * The order is the DATABASE'S — `ORDER BY created_at ASC, id ASC` inside
 * fn_desk_payment_disputes — and is not re-sorted here. A second copy of an
 * ordering rule is a second place for it to drift, and the founder's rule for
 * queues is that the oldest sits at the top.
 */
export async function fetchDisputeQueue(reason) {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_desk_payment_disputes', {
    p_reason: reason,
  });
  if (error) return fail(error.message || 'Orchid could not open the queue.');
  return {
    data: (Array.isArray(data) ? data : []).map((r) => ({
      disputeId: r.dispute_id,
      reference: r.reference_number,
      state: r.state,
      disputeType: r.dispute_type,
      filedAt: r.filed_at,
      daysOpen: r.days_open,
      tenantExplanation: r.tenant_explanation,
      landlordResponse: r.landlord_response,
      landlordRespondedAt: r.landlord_response_at,
      graceWindowEnds: r.grace_window_ends,
      // The unit, never the people. The queue is a search surface and
      // Prohibition 6 applies to it; identity is the subject lens's job, with
      // its own reason.
      propertyReference: r.property_reference,
    })),
    error: null,
  };
}

/** Record the finding. The desk decides which; nothing else is theirs to set. */
export async function settleDispute({ disputeId, finding, summary, reason }) {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_settle_a_payment_dispute', {
    p_dispute_id: disputeId,
    p_finding: finding,
    p_summary: summary,
    p_reason: reason,
  });
  if (error) return fail(error.message || 'Orchid could not record that finding.');
  const row = (Array.isArray(data) ? data : [])[0] || {};
  return { data: { disputeId: row.dispute_id, state: row.state, message: row.message }, error: null };
}

// ---------------------------------------------------------------------------
// EXIT-RATING DISPUTES -- script 368. The same desk, the same permission, one
// more queue: a tenant disputed one category of a move-out condition the
// landlord recorded. The finding's consequence is DERIVED in the database: an
// upheld finding substitutes the category's rating with the median of the
// tenant's other tenancies, or excludes the category when they have none.
// Nobody hands a desk a rating to type.
// ---------------------------------------------------------------------------

export const EXIT_FINDINGS = [
  {
    key: 'upheld',
    label: 'Uphold the dispute',
    blurb:
      'The tenant was right. The category is re-read from the tenant’s other '
      + 'tenancies, or left out of this one if they have none.',
  },
  {
    key: 'dismissed',
    label: 'Dismiss it',
    blurb: 'The landlord’s rating stands as recorded.',
  },
];

export const EXIT_CATEGORIES = {
  cleanliness: 'General cleanliness',
  walls: 'Walls and paintwork',
  fixtures: 'Fixtures and fittings',
  appliances: 'Appliances',
  outdoor: 'Garden and outdoor areas',
};

export const exitCategoryLabel = (key) => EXIT_CATEGORIES[key] || key;

/** The open exit-rating queue, oldest first -- the database's order. */
export async function fetchExitDisputeQueue(reason) {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_desk_exit_disputes', { p_reason: reason });
  if (error) return fail(error.message || 'Orchid could not open the exit-rating queue.');
  return {
    data: (Array.isArray(data) ? data : []).map((r) => ({
      disputeId: r.dispute_id,
      reference: r.reference_number,
      state: r.state,
      category: r.category,
      filedAt: r.filed_at,
      daysOpen: r.days_open,
      disputableUntil: r.disputable_until,
      tenantExplanation: r.tenant_explanation,
      landlordResponse: r.landlord_response,
      landlordRespondedAt: r.landlord_response_at,
      // The unit, never the people (Prohibition 6).
      propertyReference: r.property_reference,
      // What an upheld finding would draw on. Zero means "exclude".
      otherTenanciesRated: r.other_tenancies_rated,
    })),
    error: null,
  };
}

/** Record the finding on an exit-rating dispute. */
export async function settleExitDispute({ disputeId, finding, summary, reason }) {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_settle_an_exit_dispute', {
    p_dispute_id: disputeId,
    p_finding: finding,
    p_summary: summary,
    p_reason: reason,
  });
  if (error) return fail(error.message || 'Orchid could not record that finding.');
  const row = (Array.isArray(data) ? data : [])[0] || {};
  return {
    data: { disputeId: row.dispute_id, state: row.state,
            substitutedRating: row.substituted_rating, message: row.message },
    error: null,
  };
}
